<template>
  <Modal
    :active="isOpen"
    title="Cadastro de Método de Pagamento"
    id="popup-payment-method"
    @close="closeModal"
  >
    <ValidationObserver ref="paymentMethodConfigForm" v-slot="{ validate }">
      <vs-form @submit.prevent="() => handleSubmit(validate)">
        <div class="form-container">
          <div class="form-group-row">
            <ValidationProvider
              name="Nome"
              rules="required"
              v-slot="{ errors }"
            >
              <div class="form-group half">
                <label>Nome</label>
                <vs-input
                  v-model="formData.name"
                  placeholder="Ex: Boleto Endosso"
                  class="form-vs-input"
                  :class="{ 'error-border': errors.length > 0 }"
                />
                <span v-if="errors.length" class="error-text">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
            <div class="form-group switch-group right-aligned">
              <label>Ativo?</label>
              <vs-switch v-model="formData.isActive" />
            </div>
            <div
              v-if="formData.advanceEnabled != null"
              class="form-group switch-group right-aligned"
            >
              <label>Antecipação</label>
              <vs-switch :value="formData.advanceEnabled" disabled />
            </div>
          </div>

          <div class="form-group-row">
            <ValidationProvider
              name="Tipo pagamento"
              rules="required"
              v-slot="{ errors }"
              class="form-group third"
            >
                <label>Tipo pagamento</label>
                <v-select
                  v-model="formData.paymentMethodId"
                  :options="typeOptions"
                  :reduce="o => o.value"
                  label="label"
                  class="custom-select"
                  :class="{ 'error-border': errors.length > 0 }"
                />
                <span v-if="errors.length" class="error-text">{{ errors[0] }}</span>
            </ValidationProvider>

            <ValidationProvider
              name="Tipo pagamento"
              rules="required"
              v-slot="{ errors }"
              class="form-group third"
            >
                <label>Criação</label>
                <v-select
                  v-model="formData.policyCreationType"
                  :options="policyCreationTypeOptions"
                  :reduce="o => o.value"
                  label="label"
                  class="custom-select"
                  :class="{ 'error-border': errors.length > 0 }"
                />
                <span v-if="errors.length" class="error-text">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="form-group-row">
            <ValidationProvider
              name="Máximo de Parcelas"
              rules="required|numeric|min_value:1"
              v-slot="{ errors }"
              class="form-group third"
            >
                <label>Máximo de Parcelas</label>
                <vs-input
                  v-model.number="formData.maxInstallments"
                  type="number"
                  min="1"
                  :class="{ 'error-border': errors.length > 0 }"
                />
                <span v-if="errors.length" class="error-text">{{ errors[0] }}</span>
            </ValidationProvider>

            <div class="form-group third">
              <label>Valor mínimo da parcela (R$)</label>
              <money
                  v-model="formData.minInstallmentValue"
                  class="w-full vs-inputx vs-input--input normal"
                  name="minInstallmentValue"
                  id="minInstallmentValue"
                  v-bind="money"
                  @input.native="handleMoneyInput($event, { name: 'minInstallmentValue' })"
              ></money>
            </div>

            <div class="form-group third">
              <label>Encerrar X meses antes do fim</label>
              <vs-input
                v-model.number="formData.maxMonthsBeforePolicyEnd"
                type="number"
                min="0"
              />
            </div>
          </div>

          <div class="form-group full">
            <label>Juros por Parcela (%)</label>
            <div class="installment-additions-wrapper">
              <div
                  class="installment-item"
                  v-for="(item, index) in formData.installmentAdditions"
                  :key="index"
              >
                <label :for="'installment-' + (index + 1)">
                  Parcela {{ index + 1 }}
                </label>
                <vs-input
                    :id="'installment-' + (index + 1)"
                    type="number"
                    min="0"
                    step="0.01"
                    v-model.number="formData.installmentAdditions[index].additionPercent"
                />
              </div>
            </div>
          </div>
          </div>

          <div class="form-actions">
            <vs-button
              @click="handleSubmit"
              color="success"
              block
            >
              Salvar
            </vs-button>
            <vs-button @click="closeModal" color="danger" outline block>
              Cancelar
            </vs-button>
          </div>
      </vs-form>
    </ValidationObserver>
  </Modal>
</template>

<script>
import Modal from '@/components/Modal.vue';
import vSelect from 'vue-select';
import { Money } from 'v-money';
import {AUTO, POLICY_CREATION_TYPE} from "../../../../../constants/tipo.apolice";
import {PAYMENT_METHODS} from "../../../../../utils/constants";
import paymentMethodConfigApi from "../../../../../services/payment.method.config.api";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import notify from "../../../../../utils/notify";

const defaultFormData = {
  id: null,
  name: '',
  paymentMethodId: null,
  policyType: AUTO,
  policyCreationType: '',
  maxInstallments: null,
  minInstallmentValue: 0,
  maxMonthsBeforePolicyEnd: 0,
  isActive: false,
  advanceEnabled: null,
};

export default {
  name: 'PaymentMethodConfigModal',
  components: {ValidationObserver, ValidationProvider, Modal, vSelect, Money},
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    paymentMethodConfig: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      formData: { ...defaultFormData },
      isSubmitting: false,
      typeOptions: Object.values(PAYMENT_METHODS).map(method => ({
        label: method.name,
        value: method.id,
      })),
      policyCreationTypeOptions: Object.values(POLICY_CREATION_TYPE),
      installmentAdditions: [],
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2,
      },
    };
  },
  watch: {
    paymentMethodConfig: {
      immediate: true,
      handler(newConfig) {
        if (newConfig) {
          const cleanInstallmentAdditions = (newConfig.installmentAdditions || []).map((a) => ({
            id: a.id,
            installments: a.installments,
            additionPercent: a.additionPercent,
          }));

          this.formData = {
            ...defaultFormData,
            name: newConfig.name,
            id: newConfig.id,
            policyType: newConfig.policyType,
            policyCreationType: newConfig.policyCreationType,
            maxInstallments: newConfig.maxInstallments,
            minInstallmentValue: newConfig.minInstallmentValue,
            maxMonthsBeforePolicyEnd: newConfig.maxMonthsBeforePolicyEnd,
            isActive: newConfig.isActive,
            advanceEnabled: newConfig.advanceEnabled,
            paymentMethodId: newConfig.paymentMethodId,
            installmentAdditions: cleanInstallmentAdditions,
          };
        } else {
          this.formData = { ...defaultFormData };
        }
      },
    },
    'formData.maxInstallments'(val) {
      if (!val || isNaN(val)) {
        this.formData.installmentAdditions = [];
        return;
      }

      const count = parseInt(val, 10);
      const current = this.formData.installmentAdditions || [];

      if (current.length === count) return;

      const newList = [];

      for (let i = 0; i < count; i++) {
        const existing = current[i];
        newList.push({
          installments: i + 1,
          additionPercent:
              existing && existing.additionPercent != null
                  ? existing.additionPercent
                  : 0,
        });
      }

      this.$set(this.formData, 'installmentAdditions', newList);
    }
  },
  computed: {
    isEditMode() {
      return !!this.formData.id;
    },
  },
  methods: {
    handleMoneyInput(event, field) {
      const rawValue = event.target.value.replace(/[^\d,]/g, '');
      const normalized = rawValue.replace(',', '.');
      const parsed = parseFloat(normalized);
      if (field && field.name) {
        this.formData[field.name] = isNaN(parsed) ? 0 : parsed;
      }
    },

    async handleSubmit() {
      const observer = this.$refs.paymentMethodConfigForm;
      const isValid = await observer.validate();
      if (!isValid) return;

      this.isSubmitting = true;
      this.$vs.loading();

      try {
        const payload = this.formData;
        payload.isActive = Boolean(payload.isActive);
        payload.installmentAdditions.forEach((a) => {
          a.additionPercent = Number(a.additionPercent);
        });

        if (!this.isEditMode) delete payload.id;

        if (this.isEditMode) {
          await paymentMethodConfigApi.updatePaymentMethodConfig(payload);
        } else {
          await paymentMethodConfigApi.createPaymentMethodConfig(payload);
        }

        this.$emit('save');

        notify.notifySuccess(
          this.$vs,
          this.isEditMode
            ? 'Método de pagamento atualizado com sucesso'
            : 'Método de pagamento criado com sucesso'
        );

        this.closeModal();
      } catch (e) {
        notify.notifyFail(
          this.$vs,
          'Erro ao salvar método de pagamento. Tente novamente',
        );
      } finally {
        this.isSubmitting = false;
        this.$vs.loading.close();
      }
    },

    closeModal() {
      this.resetForm();
      this.$emit('close');
    },

    resetForm() {
      this.formData = defaultFormData;
    },
  },
};
</script>

<style>
#popup-payment-method {
  width: 100%;
}

#popup-payment-method .form-container {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

#popup-payment-method .form-group {
  display: flex;
  flex-direction: column;
  position: relative;
}

#popup-payment-method .form-vs-input {
  width: 100%;
}

#popup-payment-method label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #444;
}

#popup-payment-method .custom-select {
  width: 100%;
}

#popup-payment-method .form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

#popup-payment-method .error-border {
  border: 1px solid red !important;
}

#popup-payment-method .error-text {
  color: red;
  font-size: 12px;
  margin-top: 4px;
}

#popup-payment-method .vs-popup {
  min-width: 960px;
}

#popup-payment-method .vs-select--is-selected {
  color: #00bfed;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 4px;
}

#popup-payment-method .form-group-row {
  display: flex;
  gap: 1rem;
}

#popup-payment-method .form-group.half {
  flex: 1;
}

#popup-payment-method .form-group-row {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

#popup-payment-method .form-group.half {
  flex: 1;
}

#popup-payment-method .form-group.flex-grow {
  flex: 1;
}

#popup-payment-method .form-group-row.align-end {
  align-items: flex-end;
}

#popup-payment-method .switch-group.right-aligned {
  display: flex;
  justify-content: flex-end;
  flex: 1;
  gap: 10px;
}

#popup-payment-method .form-group.third {
  width: 100%;
  flex: 1;
}

#popup-payment-method .installment-additions-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

#popup-payment-method .installment-item {
  width: calc(33% - 10px);
}
</style>
